<!-- User Details Dialog -->
<div class="admin-dialog admin-dialog--panel" [class.admin-dialog--display-mode]="mode === Mode.disp">
  <!-- Header -->
  <div class="admin-dialog__header">
    <div class="admin-dialog__header-left">
      <span class="admin-dialog__title" i18n="@@adminSection.userDetails.title.userInfo">ユーザー情報</span>
      <div class="admin-dialog__mode-badge" [class.admin-dialog__mode-badge--edit]="mode === Mode.input">
        {{ mode === Mode.disp ? '表示モード' : '編集モード' }}
      </div>
    </div>
    <button mat-icon-button class="admin-dialog__close" (click)="onNoClick()" aria-label="Close dialog" type="button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="admin-dialog__content">
    @if (isLoading()) {
      <!-- Skeleton Loading -->
      <div class="skeleton-form">
        <!-- Identifier (CODE or Name) Skeleton -->
        <div class="skeleton-field-group">
          <div class="skeleton skeleton--label"></div>
          <div class="skeleton skeleton--field-wide"></div>
        </div>
        <!-- Birth Date Skeleton -->
        <div class="skeleton-field-group">
          <div class="skeleton skeleton--label"></div>
          <div class="skeleton skeleton--field"></div>
        </div>
        <!-- Sex Skeleton -->
        <div class="skeleton-field-group">
          <div class="skeleton skeleton--label"></div>
          <div class="skeleton skeleton--badge"></div>
        </div>
        <!-- Account Info Skeleton -->
        <div class="skeleton skeleton--info-box"></div>
      </div>
    } @else {
    <form [formGroup]="userFormGroup" class="admin-dialog__form">
      <!-- CODE Field (show only if code exists, hide if name exists) -->
      @if (userData.code) {
        <div class="admin-dialog__field-group">
          <label class="admin-dialog__field-label">CODE</label>
          <mat-form-field appearance="outline" class="admin-dialog__field admin-dialog__field--wide">
            <input matInput formControlName="code" [readonly]="mode === Mode.disp" />
            <mat-icon matSuffix>{{ mode === Mode.disp ? 'lock' : 'badge' }}</mat-icon>
          </mat-form-field>
        </div>
      }

      <!-- Name Fields (show only if name exists and no code, OR show both in edit mode if neither exists) -->
      @if (!userData.code) {
        <div class="admin-dialog__field-group">
          <label class="admin-dialog__field-label" i18n="@@adminSection.userDetails.label.name">氏名</label>
          <div class="admin-dialog__field-row">
            <mat-form-field appearance="outline" class="admin-dialog__field">
              <mat-label i18n="@@adminSection.userDetails.label.lastName">姓</mat-label>
              <input matInput formControlName="lastName" [readonly]="mode === Mode.disp" />
            </mat-form-field>
            <mat-form-field appearance="outline" class="admin-dialog__field">
              <mat-label i18n="@@adminSection.userDetails.label.firstName">名</mat-label>
              <input matInput formControlName="firstName" [readonly]="mode === Mode.disp" />
            </mat-form-field>
          </div>
        </div>
      }

      <!-- Birth Date + Sex Fields (2-column grid) -->
      <div class="admin-dialog__field-row">
        <div class="admin-dialog__field-group">
          <label class="admin-dialog__field-label" i18n="@@adminSection.userDetails.label.birthDate">生年月日</label>
          <mat-form-field appearance="outline" class="admin-dialog__field admin-dialog__field--date">
            <input matInput formControlName="birthDate"
                   [readonly]="mode === Mode.disp"
                   [placeholder]="mode === Mode.input ? 'YYYY/MM/DD' : ''" />
            <mat-icon matSuffix class="material-icons-outlined">{{ mode === Mode.disp ? 'lock' : 'cake' }}</mat-icon>
            @if (mode === Mode.input) {
              <mat-hint i18n="@@adminSection.userDetails.hint.birthDate">YYYY/MM/DD形式で入力</mat-hint>
            }
            @if (userFormGroup.get('birthDate')?.hasError('pattern')) {
              <mat-error i18n="@@adminSection.userDetails.error.birthDateFormat">
                YYYY/MM/DD形式で入力してください
              </mat-error>
            }
          </mat-form-field>
        </div>

        <div class="admin-dialog__field-group">
          <label class="admin-dialog__field-label" i18n="@@adminSection.userDetails.label.sex">性別</label>
          @if (mode === Mode.disp) {
            <div class="admin-dialog__badge admin-dialog__badge--info">
              {{ userData.sex | sexLang : 'jp' }}
            </div>
          } @else {
            <mat-radio-group formControlName="sex" class="admin-dialog__radio-group">
              @for (option of sexOptions; track option.value) {
                <mat-radio-button [value]="option.value" color="primary">
                  {{ option.display }}
                </mat-radio-button>
              }
            </mat-radio-group>
          }
        </div>
      </div>

      <!-- Readonly System Info -->
      <div class="admin-dialog__readonly-section">
        <h5 class="admin-dialog__readonly-title" i18n="@@adminSection.userDetails.label.accountInfo">アカウント情報</h5>

        <div class="admin-dialog__readonly-row">
          <span class="admin-dialog__readonly-label" i18n="@@adminSection.userDetails.label.accountId">アカウントID</span>
          <span class="admin-dialog__readonly-value">{{ userFormGroup.get('accountId')?.value }}</span>
        </div>

        <div class="admin-dialog__readonly-row">
          <span class="admin-dialog__readonly-label" i18n="@@adminSection.userDetails.label.contractStartAt">利用開始日</span>
          <span class="admin-dialog__readonly-value">{{ userFormGroup.get('contractStartAt')?.value }}</span>
        </div>

        @if (userFormGroup.get('contractEndAt')?.value) {
          <div class="admin-dialog__readonly-row">
            <span class="admin-dialog__readonly-label" i18n="@@adminSection.userDetails.label.contractEndAt">契約終了日</span>
            <span class="admin-dialog__readonly-value">{{ userFormGroup.get('contractEndAt')?.value }}</span>
          </div>
        }
      </div>
    </form>
    }
  </div>

  <!-- Footer -->
  <div class="admin-dialog__footer">
    @if (mode === Mode.disp) {
      <button mat-stroked-button (click)="onNoClick()">
        <span i18n="@@adminSection.userDetails.button.close">閉じる</span>
      </button>
    } @else {
      <button mat-stroked-button color="warn" (click)="onNoClick()">
        <span i18n="@@adminSection.userDetails.button.discard">破棄</span>
      </button>
    }
    <button mat-flat-button color="primary" [disabled]="isSending()" (click)="onEditOrUploadClicked()">
      @if (isSending()) {
        <mat-spinner diameter="18"></mat-spinner>
      }
      <span i18n="@@adminSection.userDetails.button.editOrSave">{{ mode === Mode.disp ? '編集' : '保存' }}</span>
    </button>
  </div>
</div>
